import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box, Button, Chip, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tab, Tabs, Tooltip, Typography, useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import QuizIcon from "@mui/icons-material/Quiz";
import AddIcon from "@mui/icons-material/Add";
import UnpublishedOutlinedIcon from "@mui/icons-material/UnpublishedOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import {
    VLearnEmptyState, vlearnSurface, vlearnType
} from "../../components/vlearn/vlearnUi";
import ConfirmDialog from "../../components/popups/ConfirmDialog";
import CourseDetailsForm from "../../components/vlearn/CourseDetailsForm";
import CurriculumEditor from "../../components/vlearn/CurriculumEditor";
import AudiencePicker from "../../components/vlearn/AudiencePicker";
import CourseManagersPanel from "../../components/vlearn/CourseManagersPanel";
import AnnouncementsPanel from "../../components/vlearn/AnnouncementsPanel";
import {
    archiveVLearnCourse, closeCourse, deleteVLearnCourse, duplicateVLearnCourse,
    getVLearnCourse, refreshVLearnCourse,
    listVLearnCategories, listVLearnQuizzes, publishVLearnCourse,
    saveVLearnQuiz, unpublishVLearnCourse
} from "./vlearnSlice";

/**
 * V-Learning — the authoring shell for one course.
 *
 * ── Why the tabs, rather than one long page ──────────────────────────────────────────
 *
 * Authoring a course is four unrelated decisions — what it says, what it contains, who has
 * to take it, and who may edit it — and each is a different question asked of a different
 * person at a different moment. A single scrolling form makes the audience rule, which is
 * the one with compliance consequences, something an author scrolls past.
 *
 * ── Why the server's refusal is shown verbatim ───────────────────────────────────────
 *
 * `publishCourse` answers 422 with one of two specific sentences ("needs at least one
 * lesson", "needs an audience"), and `deleteCourse` with a third that names archiving as the
 * alternative. Those sentences are the only thing that tells an author which condition they
 * failed, so they are surfaced as they arrive. A generic "action failed" would leave someone
 * clicking Publish repeatedly with no way to discover what is missing.
 */

/** The status chip's colours, taken from the module's own tokens rather than MUI's palette. */
const STATUS_STYLES = {
    draft: { bg: 'var(--lightGrey, #ededed)', fg: '#3a3a3a', key: 'VLEARN_COURSE_STATUS_DRAFT' },
    published: { bg: 'rgba(58, 156, 96, 0.14)', fg: '#1F7A4C', key: 'VLEARN_COURSE_STATUS_PUBLISHED' },
    archived: { bg: 'rgba(0, 0, 0, 0.07)', fg: '#5a6070', key: 'VLEARN_COURSE_STATUS_ARCHIVED' }
}

/**
 * The server's own message, whichever shape the failure arrived in.
 *
 * `postRequest` resolves `{ responseError: error.response }`, so the body is at `.data`; the
 * multipart uploaders reject with the raw axios error, whose body is at `.response.data`.
 * Both are read here so a caller never has to know which thunk it called.
 */
const serverMessage = (payload) => {
    const error = payload && payload.responseError
    if (!error) return null
    const data = error.data || (error.response && error.response.data)
    return (data && data.error) || null
}

export default function CourseEditor({ idCourse, onBack, onOpenDashboard, onPreview, onEditQuiz, initialTab }) {

    const dispatch = useDispatch()

    const course = useSelector((state) => state.vlearn.course)
    const sections = useSelector((state) => state.vlearn.sections)
    const access = useSelector((state) => state.vlearn.access)
    const quizzes = useSelector((state) => state.vlearn.quizzes)
    const saveState = useSelector((state) => state.vlearn.saveState)
    const lang = useSelector((state) => state.settings.lang)
    const isMobileRedux = useSelector((state) => state.settings.isMobile)
    const theme = useTheme()
    // Called unconditionally: behind a `||` the hook would be skipped whenever the Redux
    // flag already said "phone", which breaks the rules of hooks.
    const isNarrowQuery = useMediaQuery(theme.breakpoints.down('sm'))
    const isNarrow = isMobileRedux === 2 || isNarrowQuery
    // Anchor for the phone's overflow menu; null when it is closed.
    const [moreAnchor, setMoreAnchor] = useState(null)

    /**
     * Which tab to open on.
     *
     * Named rather than an index, so a caller does not have to know that Quizzes happens to be
     * the fifth tab — inserting a tab would silently send them somewhere else.
     *
     * This matters because the editor REMOUNTS on the way back from the quiz editor: the
     * screen state changes name, `useState(0)` ran again, and an author who opened a quiz from
     * the Quizzes tab and closed it landed on Details, with their quiz nowhere in sight.
     */
    const TAB_INDEX = { details: 0, content: 1, audience: 2, instructors: 3, quizzes: 4, announcements: 5 }
    const [tab, setTab] = useState(() => TAB_INDEX[initialTab] ?? 0)
    /** Which destructive action is awaiting confirmation. Null while no dialog is open. */
    const [confirming, setConfirming] = useState(null)
    /**
     * Creating a quiz belongs here, in the Quizzes tab.
     *
     * It used to live in the lesson dialog: choosing lesson type "Quiz" offered a
     * "Create a new quiz" button beside the picker. That made the quiz a side effect of
     * writing a lesson, so authors could not create one without first inventing a lesson to
     * hang it on, and the two jobs — write the assessment, then place it in the curriculum —
     * were tangled into one dialog. The lesson dialog now only ATTACHES an existing quiz.
     *
     * Created with a placeholder title and opened straight in the quiz editor, which is where
     * the title field is: asking for a name in a dialog first, then asking again in the
     * editor, would be the same question twice.
     */
    const onCreateQuiz = async () => {
        const response = await dispatch(saveVLearnQuiz({
            id_course: courseId, title: Localize("VLEARN_QUIZ_NEW_TITLE", lang)
        }))
        const data = response.payload && response.payload.responseData
        if (!data || data.status !== 1) {
            toast.error(Localize("VLEARN_ACTION_FAILED", lang))
            return
        }
        await dispatch(listVLearnQuizzes({ id_course: courseId }))
        toast.success(Localize("VLEARN_QUIZ_CREATED", lang))
        if (onEditQuiz) onEditQuiz(data.id)
    }

    const [busy, setBusy] = useState(false)

    /**
     * Whether this instance is still on screen.
     *
     * Every action below awaits a dispatch and then touches local state or raises a toast.
     * Without this the author would get a toast for a course they have already left.
     */
    const mounted = useRef(true)
    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    /**
     * The course id already requested.
     *
     * React 18's StrictMode mounts every effect, tears it down and mounts it again in
     * development. Without the ref the course would be fetched twice on every entry, and the
     * blocking loader `getVLearnCourse` carries would flash twice with it. The teardown
     * below still runs on the simulated unmount — the in-flight response lands after it and
     * repopulates the store, which is why the guard is safe to keep.
     */
    const requested = useRef(null)
    useEffect(() => {
        if (!idCourse || requested.current === idCourse) return
        requested.current = idCourse
        dispatch(getVLearnCourse({ id_course: idCourse }))
        // The category list belongs to the module, not to this course, and the details form
        // cannot render its Select without it.
        dispatch(listVLearnCategories({}))
    }, [dispatch, idCourse])

    // Separate from the fetch so it fires on a genuine unmount rather than on every change
    // of `idCourse`, where the fetch above already replaces the whole course-scoped slice.
    useEffect(() => () => { dispatch(closeCourse()) }, [dispatch])

    const canEdit = !!(access && access.canEdit)
    const courseId = course ? course.id : null

    // `listQuizzes` requires edit rights server-side, so it is only worth asking for once
    // the course's access flags have arrived and say yes.
    useEffect(() => {
        if (!courseId || !canEdit) return
        dispatch(listVLearnQuizzes({ id_course: courseId }))
    }, [dispatch, courseId, canEdit])

    const reload = useCallback(
        // The loader-free twin: this fires after every autosave, and the full-screen spinner
        // on each one made the Details tab unusable. The initial load below still blocks.
        () => dispatch(refreshVLearnCourse({ id_course: idCourse })), [dispatch, idCourse])

    /**
     * Runs one course-level action and reports its outcome.
     *
     * The server's sentence wins over any message this screen could invent — see the file
     * comment. `VLEARN_ACTION_FAILED` is only the fallback for a transport failure that
     * carried no body at all.
     */
    const runAction = useCallback(async (thunk, successKey) => {
        setBusy(true)
        const response = await dispatch(thunk({ id_course: idCourse }))
        if (!mounted.current) return null
        setBusy(false)
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            toast.success(Localize(successKey, lang))
            return data
        }
        toast.error(serverMessage(response.payload) || Localize("VLEARN_ACTION_FAILED", lang))
        return null
    }, [dispatch, idCourse, lang])

    const onPublish = async () => {
        const data = await runAction(publishVLearnCourse, "VLEARN_PUBLISHED")
        if (data) reload()
    }

    const onConfirmed = async () => {
        const action = confirming
        setConfirming(null)
        if (action === 'unpublish') {
            const data = await runAction(unpublishVLearnCourse, "VLEARN_UNPUBLISHED")
            if (data) reload()
        } else if (action === 'archive') {
            const data = await runAction(archiveVLearnCourse, "VLEARN_ARCHIVED")
            if (data) reload()
        } else if (action === 'delete') {
            const data = await runAction(deleteVLearnCourse, "VLEARN_DELETED")
            // Nothing is left to render once the course is gone, so leaving is part of the
            // action rather than something the author has to do next.
            if (data && onBack) onBack()
        } else if (action === 'duplicate') {
            const data = await runAction(duplicateVLearnCourse, "VLEARN_DUPLICATED")
            // The copy is a different course with its own id, and this editor is bound to
            // one. Returning to the listing is the only honest destination — opening the copy
            // would silently discard whatever tab the author was on.
            if (data && onBack) onBack()
        }
    }

    /** The autosave indicator, in the same three states V-Resume's uses. */
    const saveIndicator = useMemo(() => {
        if (!canEdit) return null
        const shared = {
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            fontSize: '0.75rem', color: 'var(--text-muted, #8b92a0)'
        }
        if (saveState === 'saving') return (
            <Box component="span" sx={shared}>
                <CloudSyncOutlinedIcon sx={{ fontSize: 15 }} /><Langs str="VLEARN_SAVING" />
            </Box>
        )
        if (saveState === 'error') return (
            <Box component="span" sx={{ ...shared, color: 'var(--red, rgb(251,105,98))' }}>
                <ErrorOutlineIcon sx={{ fontSize: 15 }} /><Langs str="VLEARN_SAVE_ERROR" />
            </Box>
        )
        if (saveState === 'saved') return (
            <Box component="span" sx={{ ...shared, color: 'var(--green, rgb(58,156,96))' }}>
                <CloudDoneOutlinedIcon sx={{ fontSize: 15 }} /><Langs str="VLEARN_SAVED" />
            </Box>
        )
        return (
            <Box component="span" sx={shared}>
                <CloudDoneOutlinedIcon sx={{ fontSize: 15 }} /><Langs str="VLEARN_AUTOSAVE_ON" />
            </Box>
        )
    }, [canEdit, saveState])

    const backButton = (
        <Tooltip title={Localize("VLEARN_BACK", lang)} arrow>
            <IconButton size="small" onClick={onBack} aria-label={Localize("VLEARN_BACK", lang)}>
                <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
        </Tooltip>
    )

    // The course has not arrived yet. `getVLearnCourse` takes the global loader, so a second
    // local spinner here would be two spinners for one wait.
    /*
     * A failed or refused `getVLearnCourse` used to `return null` here — an empty grey page
     * with no message and no way back. The slice's reducer bails on any non-`status:1`
     * response (a 403, a deleted course, a dropped connection) and the global spinner has
     * already been dismissed by then, so `course` simply stays null forever.
     *
     * The same empty state the no-edit-rights branch uses below is the right answer: it says
     * what happened and it has a back button.
     */
    if (!course) {
        return (
            <Box sx={{ paddingTop: '20px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {backButton}
                </Box>
                <Box sx={vlearnSurface.card}>
                    <VLearnEmptyState
                        title={<Langs str="VLEARN_COURSE_UNAVAILABLE_TITLE" />}
                        description={<Langs str="VLEARN_COURSE_UNAVAILABLE_DESC" />}
                        action={backButton}
                    />
                </Box>
            </Box>
        )
    }

    // A viewer who reached this screen without edit rights gets an explanation and a way out,
    // never a form whose every control is inert. The server refuses the writes regardless;
    // this is about not presenting an editor that cannot edit.
    if (!canEdit) {
        return (
            <Box sx={{ padding: { xs: '12px', sm: '18px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {backButton}
                    <Typography component="h1" sx={vlearnType.pageTitle}>{course.title}</Typography>
                </Box>
                <Box sx={vlearnSurface.card}>
                    <VLearnEmptyState
                        icon={<LockOutlinedIcon />}
                        title={<Langs str="VLEARN_NO_EDIT_TITLE" />}
                        description={<Langs str="VLEARN_NO_EDIT_DESC" />}
                        action={
                            <Button variant="contained" onClick={onBack}
                                sx={{ textTransform: 'none' }}>
                                <Langs str="VLEARN_BACK_TO_CATALOG" />
                            </Button>
                        }
                    />
                </Box>
            </Box>
        )
    }

    const status = STATUS_STYLES[course.status] || STATUS_STYLES.draft
    const isPublished = course.status === 'published'
    const isArchived = course.status === 'archived'

    /** One action button, disabled states explained rather than left silent. */
    const actionButton = (labelKey, icon, onClick, options = {}) => {
        const control = (
            // `outlined`, not `text`: a row of text buttons reads as a row of links, and an
            // author cannot tell what is clickable. The primary action stays `contained`.
            <Button size="small" variant={options.variant || 'outlined'} onClick={onClick}
                disabled={busy || !!options.disabledReasonKey}
                startIcon={icon}
                sx={{
                    // `size="small"` is a 30px button. This toolbar wraps onto three rows on
                    // a phone and includes Delete, so every one of them gets the tap-target
                    // floor rather than being a 30px strip beside another 30px strip.
                    textTransform: 'none', fontSize: '0.8rem', minHeight: 44,
                    borderColor: options.danger
                        ? 'rgba(251,105,98,0.5)' : 'rgba(0, 40, 128, 0.18)',
                    color: options.danger ? 'var(--red, rgb(251,105,98))' : undefined,
                    '&:hover': options.variant === 'contained' ? undefined : {
                        borderColor: options.danger
                            ? 'var(--red, rgb(251,105,98))' : 'var(--orange, rgb(255,144,21))',
                        backgroundColor: options.danger
                            ? 'rgba(251,105,98,0.06)' : 'rgba(255,144,21,0.06)'
                    },
                    ...(options.variant === 'contained' ? {
                        backgroundColor: 'var(--orange, rgb(255,144,21))',
                        '&:hover': { backgroundColor: '#e07f0d' }
                    } : {}),
                    '&:focus-visible': {
                        outline: '2px solid var(--orange, rgb(255,144,21))', outlineOffset: '2px'
                    }
                }}>
                <Langs str={labelKey} />
            </Button>
        )
        if (!options.disabledReasonKey) return control
        // A disabled control that says nothing is a dead end. The span is required: MUI
        // cannot attach a tooltip to a disabled button, which fires no pointer events.
        return (
            <Tooltip title={Localize(options.disabledReasonKey, lang)} arrow>
                <span>{control}</span>
            </Tooltip>
        )
    }

    /*
     * The toolbar as data, so the desktop row and the phone's overflow menu are two
     * renderings of ONE list. Previously the six actions were written out twice over
     * (once inline, once in a phone-only wrapper) and drifted apart.
     */
    const viewActions = [
        onPreview ? {
            labelKey: 'VLEARN_PREVIEW',
            icon: <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />,
            onClick: onPreview, options: {}
        } : null,
        onOpenDashboard ? {
            labelKey: 'VLEARN_DASHBOARD',
            icon: <BarChartOutlinedIcon sx={{ fontSize: 16 }} />,
            onClick: onOpenDashboard,
            options: {
                disabledReasonKey: access.canViewDashboard ? null : 'VLEARN_DASHBOARD_NO_ACCESS'
            }
        } : null
    ].filter(Boolean)

    const lifecycleActions = [
        {
            labelKey: 'VLEARN_DUPLICATE',
            icon: <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />,
            onClick: () => setConfirming('duplicate'), options: {}
        },
        {
            labelKey: 'VLEARN_ARCHIVE',
            icon: <ArchiveOutlinedIcon sx={{ fontSize: 16 }} />,
            onClick: () => setConfirming('archive'),
            options: { disabledReasonKey: isArchived ? 'VLEARN_ALREADY_ARCHIVED' : null }
        }
    ]

    const publishAction = isPublished
        ? {
            labelKey: 'VLEARN_UNPUBLISH',
            icon: <UnpublishedOutlinedIcon sx={{ fontSize: 16 }} />,
            onClick: () => setConfirming('unpublish'), options: {}
        }
        : {
            labelKey: 'VLEARN_PUBLISH',
            icon: <PublicOutlinedIcon sx={{ fontSize: 16 }} />,
            onClick: onPublish, options: { variant: 'contained' }
        }

    const deleteAction = {
        labelKey: 'VLEARN_DELETE',
        icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />,
        onClick: () => setConfirming('delete'),
        options: {
            danger: true,
            disabledReasonKey: access.isOwner ? null : 'VLEARN_DELETE_OWNER_ONLY'
        }
    }

    // Delete goes LAST in the menu, after a divider's worth of distance in the list.
    const secondaryActions = [...viewActions, ...lifecycleActions, deleteAction]

    const tabs = [
        { key: 'VLEARN_TAB_DETAILS' },
        { key: 'VLEARN_TAB_CONTENT' },
        { key: 'VLEARN_TAB_AUDIENCE' },
        { key: 'VLEARN_TAB_INSTRUCTORS' },
        { key: 'VLEARN_TAB_QUIZZES' },
        // Announcements had NO authoring surface at all: `AnnouncementsPanel` was a complete
        // 540-line component — compose dialog, pin, notify-by-email, read counts, delete —
        // that nothing imported. Learners could read announcements in the player's drawer,
        // and no instructor could ever post one. Same class of bug as QuizEditor.
        { key: 'VLEARN_TAB_ANNOUNCEMENTS' }
    ]

    return (
        <Box sx={{ padding: { xs: '10px', sm: '18px' }, maxWidth: 1180, margin: '0 auto' }}>

            {/* ── Sticky header ───────────────────────────────────────────────
                Sticky because the publish controls and the save indicator are the two things
                an author looks for after any edit, and both live here. */}
            <Box sx={{
                position: 'sticky', top: 0, zIndex: 3,
                // An opaque background is what makes "sticky" work at all — content scrolls
                // BEHIND this bar, and a transparent one would show it through.
                backgroundColor: 'var(--surface-ground, #f7f8fa)',
                // The top spacing lives here rather than on the scroll container: sticky
                // offsets are measured from the container's padding box, so a padding-top up
                // there left a band above this bar that scrolling fields showed through.
                // Owning the padding means the bar covers everything it is pinned over.
                paddingTop: '20px',
                paddingBottom: '10px', marginBottom: '14px',
                borderBottom: '1px solid rgba(0,0,0,0.06)'
            }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    flexWrap: 'wrap', minWidth: 0
                }}>
                    {backButton}
                    <Typography component="h1" sx={{ ...vlearnType.pageTitle, minWidth: 0 }}>
                        {course.title}
                    </Typography>
                    <Chip size="small" label={<Langs str={status.key} />}
                        sx={{
                            backgroundColor: status.bg, color: status.fg,
                            fontWeight: 600, fontSize: '0.72rem', borderRadius: '7px'
                        }} />
                    <Box sx={{ flex: '1 1 auto' }} />
                    {saveIndicator}
                </Box>

                {/*
                  * SIX EQUAL BUTTONS DO NOT FIT A PHONE.
                  *
                  * Wrapped onto a 360px screen this row became three lines — about a third of
                  * the viewport spent on a toolbar before any of the course was visible, and
                  * it is STICKY, so that third never came back. Below `sm` only the action an
                  * author actually reaches for stays out (Publish / Unpublish); the rest move
                  * into an overflow menu, which also puts Delete behind a deliberate second
                  * tap. One list drives both renderings so they cannot drift apart.
                  */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    flexWrap: 'wrap', mt: 1
                }}>
                    {isNarrow ? (
                        <>
                            {actionButton(publishAction.labelKey, publishAction.icon,
                                publishAction.onClick, publishAction.options)}
                            <Box sx={{ flex: '1 1 auto' }} />
                            <IconButton
                                onClick={(event) => setMoreAnchor(event.currentTarget)}
                                aria-label={Localize('VLEARN_MORE_ACTIONS', lang)}
                                aria-haspopup="menu"
                                sx={{ width: 44, height: 44 }}>
                                <MoreVertIcon />
                            </IconButton>
                            <Menu anchorEl={moreAnchor} open={!!moreAnchor}
                                onClose={() => setMoreAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                                {secondaryActions.map((action) => (
                                    <MenuItem key={action.labelKey}
                                        disabled={busy || !!action.options.disabledReasonKey}
                                        onClick={() => { setMoreAnchor(null); action.onClick() }}
                                        sx={{
                                            minHeight: 48, gap: 1,
                                            color: action.options.danger
                                                ? 'var(--red, rgb(251,105,98))' : undefined
                                        }}>
                                        <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                                            {action.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Langs str={action.labelKey} />}
                                            /* A disabled row that explains nothing is a dead
                                               end, and a phone has no tooltip to carry it. */
                                            secondary={action.options.disabledReasonKey
                                                ? Localize(action.options.disabledReasonKey, lang)
                                                : null}
                                            secondaryTypographyProps={{ sx: { fontSize: '0.72rem' } }}
                                        />
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    ) : (
                        <>
                            {viewActions.map((action) => (
                                <React.Fragment key={action.labelKey}>
                                    {actionButton(action.labelKey, action.icon,
                                        action.onClick, action.options)}
                                </React.Fragment>
                            ))}
                            {viewActions.length ? (
                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
                            ) : null}
                            {lifecycleActions.map((action) => (
                                <React.Fragment key={action.labelKey}>
                                    {actionButton(action.labelKey, action.icon,
                                        action.onClick, action.options)}
                                </React.Fragment>
                            ))}
                            {actionButton(publishAction.labelKey, publishAction.icon,
                                publishAction.onClick, publishAction.options)}
                            {actionButton(deleteAction.labelKey, deleteAction.icon,
                                deleteAction.onClick, deleteAction.options)}
                        </>
                    )}
                </Box>
            </Box>

            <Tabs
                value={tab}
                onChange={(event, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label={Localize("VLEARN_TABS_LABEL", lang)}
                sx={{
                    minHeight: 40, mb: 2,
                    '& .MuiTab-root': {
                        textTransform: 'none', fontSize: '0.85rem', minHeight: 40,
                        '&:focus-visible': {
                            outline: '2px solid var(--orange, rgb(255,144,21))',
                            outlineOffset: '-2px'
                        }
                    },
                    '& .Mui-selected': { color: 'var(--orange, rgb(255,144,21))' },
                    '& .MuiTabs-indicator': { backgroundColor: 'var(--orange, rgb(255,144,21))' }
                }}
            >
                {tabs.map((item, index) => (
                    <Tab key={item.key} label={<Langs str={item.key} />}
                        id={`vlearn-tab-${index}`} aria-controls={`vlearn-panel-${index}`} />
                ))}
            </Tabs>

            <Box role="tabpanel" id="vlearn-panel-0" aria-labelledby="vlearn-tab-0"
                hidden={tab !== 0}>
                {tab === 0 ? <CourseDetailsForm course={course} onSaved={reload} /> : null}
            </Box>

            <Box role="tabpanel" id="vlearn-panel-1" aria-labelledby="vlearn-tab-1"
                hidden={tab !== 1}>
                {tab === 1 ? (
                    <CurriculumEditor sections={sections} idCourse={idCourse}
                        onEditQuiz={onEditQuiz} />
                ) : null}
            </Box>

            <Box role="tabpanel" id="vlearn-panel-2" aria-labelledby="vlearn-tab-2"
                hidden={tab !== 2}>
                {tab === 2 ? <AudiencePicker idCourse={idCourse} /> : null}
            </Box>

            <Box role="tabpanel" id="vlearn-panel-3" aria-labelledby="vlearn-tab-3"
                hidden={tab !== 3}>
                {tab === 3 ? <CourseManagersPanel idCourse={idCourse} /> : null}
            </Box>

            <Box role="tabpanel" id="vlearn-panel-5" aria-labelledby="vlearn-tab-5"
                hidden={tab !== 5}>
                {tab === 5 ? (
                    <AnnouncementsPanel idCourse={idCourse} canEdit={access.canEdit} />
                ) : null}
            </Box>

            <Box role="tabpanel" id="vlearn-panel-4" aria-labelledby="vlearn-tab-4"
                hidden={tab !== 4}>
                {tab === 4 ? (
                    <Box sx={{ ...vlearnSurface.panel }}>
                        <Typography component="h2" sx={{ ...vlearnType.sectionTitle, mb: 0.5 }}>
                            <Langs str="VLEARN_QUIZZES_TITLE" />
                        </Typography>
                        <Typography sx={{ ...vlearnType.body, mb: 2 }}>
                            <Langs str="VLEARN_QUIZZES_HELP" />
                        </Typography>

                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                            onClick={onCreateQuiz}
                            sx={{
                                textTransform: 'none', fontSize: '0.8rem', mb: 2,
                                backgroundColor: 'var(--orange, rgb(255,144,21))',
                                '&:hover': { backgroundColor: '#e07f0d' }
                            }}
                        >
                            <Langs str="VLEARN_CREATE_QUIZ" />
                        </Button>

                        {(quizzes || []).length ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {quizzes.map(quiz => (
                                    <Box key={quiz.id} sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                        flexWrap: 'wrap',
                                        padding: '10px 12px', borderRadius: '10px',
                                        border: '1px solid rgba(0,0,0,0.08)'
                                    }}>
                                        <QuizIcon sx={{ fontSize: 18, color: '#8E4EC6' }} />
                                        <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
                                            <Typography component="h3" sx={vlearnType.cardTitle}>
                                                {quiz.title}
                                            </Typography>
                                            <Typography sx={vlearnType.meta}>
                                                <Langs
                                                    str={Number(quiz.questions_count) === 1
                                                        ? 'VLEARN_QUESTION_COUNT_ONE'
                                                        : 'VLEARN_QUESTION_COUNT_MANY'}
                                                    params={{ count: Number(quiz.questions_count) || 0 }}
                                                />
                                                {' · '}
                                                <Langs str="VLEARN_QUIZ_PASSING"
                                                    params={{ percent: quiz.passing_percent }} />
                                            </Typography>
                                        </Box>
                                        <Button size="small" variant="outlined"
                                            onClick={() => onEditQuiz && onEditQuiz(quiz.id)}
                                            sx={{ textTransform: 'none', fontSize: '0.78rem' }}>
                                            <Langs str="VLEARN_EDIT" />
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <VLearnEmptyState
                                icon={<QuizIcon />}
                                title={<Langs str="VLEARN_NO_QUIZ_TITLE" />}
                                description={<Langs str="VLEARN_NO_QUIZ_DESC" />}
                                action={(
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                                        onClick={onCreateQuiz}
                                        sx={{ textTransform: 'none', fontSize: '0.82rem' }}
                                    >
                                        <Langs str="VLEARN_CREATE_QUIZ" />
                                    </Button>
                                )}
                            />
                        )}
                    </Box>
                ) : null}
            </Box>

            {/* ── Confirmations ───────────────────────────────────────────────
                Each states the consequence in words. Delete says what the server will answer
                once anyone has started the course, and names archiving, because an author who
                learns that only from a 422 has already decided to destroy the record. */}
            <ConfirmDialog
                open={!!confirming}
                danger={confirming === 'delete'}
                loading={busy}
                title={<Langs str={
                    confirming === 'delete' ? 'VLEARN_DELETE_CONFIRM_TITLE'
                        : confirming === 'archive' ? 'VLEARN_ARCHIVE_CONFIRM_TITLE'
                            : confirming === 'unpublish' ? 'VLEARN_UNPUBLISH_CONFIRM_TITLE'
                                : 'VLEARN_DUPLICATE_CONFIRM_TITLE'
                } />}
                message={<Langs str={
                    confirming === 'delete' ? 'VLEARN_DELETE_CONFIRM_BODY'
                        : confirming === 'archive' ? 'VLEARN_ARCHIVE_CONFIRM_BODY'
                            : confirming === 'unpublish' ? 'VLEARN_UNPUBLISH_CONFIRM_BODY'
                                : 'VLEARN_DUPLICATE_CONFIRM_BODY'
                } />}
                confirmLabel={<Langs str={
                    confirming === 'delete' ? 'VLEARN_DELETE'
                        : confirming === 'archive' ? 'VLEARN_ARCHIVE'
                            : confirming === 'unpublish' ? 'VLEARN_UNPUBLISH'
                                : 'VLEARN_DUPLICATE'
                } />}
                onConfirm={onConfirmed}
                onCancel={() => setConfirming(null)}
            />
        </Box>
    )
}
